import TiptapTable from "@tiptap/extension-table";
import { Node as PMNode } from "@tiptap/pm/model";
import { EditorState, Plugin, PluginKey, Transaction } from "@tiptap/pm/state";
import { selectedRect, TableMap } from "@tiptap/pm/tables";
import { Decoration, DecorationSet, EditorView } from "@tiptap/pm/view";

import { findParentNodeClosestToPos, findTable, getSelectedColumnIndex, tableHasSpans } from "./utils";

/** Floor for column width, both on drag-resize and when redistributing width to keep the table on the page. Matches table.css's `min-w-[100px]` on td/th. */
const kMinColumnWidth = 100;

/**
 * Fallback column cap used only when the table's container can't be measured (DOM not
 * mounted). Normally addColumnBefore/After are gated by live geometry instead — see
 * withColumnCountGuard — since a fixed count doesn't account for kMinColumnWidth: at 100px/col,
 * a page ~680px wide only fits ~6 columns, not 8, so a static cap and the width floor could
 * (and did) disagree and let the table overflow the page.
 */
const kMaxColumnCountFallback = 8;

const columnWidthGuardKey = new PluginKey("tableColumnWidthGuard");

export type TableAlign = "left" | "center" | "right";
const kDefaultTableAlign: TableAlign = "left";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    // Separate key from @tiptap/extension-table's own `table` augmentation —
    // TS interface merging requires identical types for re-declared properties,
    // so a new command can't be added under the same `table` key from here.
    tableCustom: {
      /** Swap the grip-selected column with its left/right neighbour. No-ops on tables with col/rowspans. */
      moveColumn: (direction: "left" | "right") => ReturnType;
      /** Position a table narrower than its container within it. No visible effect at full width. */
      setTableAlign: (align: TableAlign) => ReturnType;
    };
  }
}

export const Table = TiptapTable.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      // Only meaningful once the table's own width is under its container's — see
      // clampOverflowToMaxWidth's doc comment for why that's now an accepted state. Applied to
      // the live DOM via syncTableAlign (the table's custom nodeview bypasses renderHTML, see
      // its comment), not read here.
      align: {
        default: kDefaultTableAlign,
      },
      // The editor's own measured 100%-width reference (getTableContainerWidth's return value,
      // in CSS px) at the time colwidth was last written — stamped by writeColumnWidths.
      // pdf-report-core has no other way to know what "100%" meant in this table's own
      // coordinate space, so without it a table left narrower than its container always
      // rendered stretched to fill the PDF page instead. See pdf-report-core's
      // _getTableColumnWidths for the consuming side.
      containerWidth: {
        default: null,
      },
    };
  },

  addCommands() {
    const parentCommands = this.parent?.() ?? {};

    // No-ops (rather than erroring) once another column wouldn't fit at kMinColumnWidth —
    // measured live against the table's actual container, not a fixed column count (see
    // kMaxColumnCountFallback's comment for why a fixed count doesn't work here).
    const withColumnCountGuard =
      (command: (...args: any[]) => any) =>
      (...args: any[]) =>
      (props: any) => {
        const table = findTable(props.state.selection);
        if (table) {
          const columnCount = TableMap.get(table.node).width;
          const availableWidth = getTableContainerWidth(props.view, table.pos);

          const tooManyColumns =
            availableWidth !== null
              ? (columnCount + 1) * kMinColumnWidth > availableWidth
              : columnCount >= kMaxColumnCountFallback;

          if (tooManyColumns) return false;
        }
        return command(...args)(props);
      };

    return {
      ...parentCommands,
      addColumnBefore: withColumnCountGuard(parentCommands.addColumnBefore!),
      addColumnAfter: withColumnCountGuard(parentCommands.addColumnAfter!),
      addRowBefore: withRowStyleCopy(parentCommands.addRowBefore!, (rect) => ({
        newRow: rect.top,
        refRow: rect.top + 1,
      })),
      addRowAfter: withRowStyleCopy(parentCommands.addRowAfter!, (rect) => ({
        newRow: rect.bottom,
        refRow: rect.bottom - 1,
      })),
      moveColumn:
        (direction: "left" | "right") =>
        ({ state, dispatch }: any) => {
          const table = findTable(state.selection);
          if (!table) return false;

          const map = TableMap.get(table.node);
          const col = getSelectedColumnIndex(state.selection);
          if (col === null) return false;

          const targetCol = direction === "left" ? col - 1 : col + 1;
          if (targetCol < 0 || targetCol >= map.width) return false;

          // Reordering columns under col/rowspans requires shuffling TableMap slots,
          // not just swapping array entries — not supported yet, so bail out clean.
          if (tableHasSpans(table.node)) return false;

          if (dispatch) {
            const newRows: any[] = [];
            table.node.forEach((row: any) => {
              const cells: any[] = [];
              row.forEach((cell: any) => cells.push(cell));
              [cells[col], cells[targetCol]] = [cells[targetCol], cells[col]];
              newRows.push(row.type.create(row.attrs, cells));
            });

            const newTable = table.node.type.create(table.node.attrs, newRows);
            // Swapping whole cells (colwidth included) already produces correct column widths —
            // tag the transaction so appendTransaction's resize-redistribution doesn't mistake
            // this same-size change for a drag-resize and "correct" already-correct widths,
            // pulling width from the untouched third+ column in the process.
            dispatch(
              state.tr
                .setMeta(columnWidthGuardKey, true)
                .replaceWith(table.pos, table.pos + table.node.nodeSize, newTable)
            );
          }

          return true;
        },
      setTableAlign:
        (align: TableAlign) =>
        ({ state, dispatch }: any) => {
          const table = findTable(state.selection);
          if (!table) return false;

          if (dispatch) {
            dispatch(state.tr.setNodeMarkup(table.pos, undefined, { ...table.node.attrs, align }));
          }

          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const parentPlugins = this.parent?.() ?? [];

    // Drag-resize (via prosemirror-tables' columnResizing plugin) only ever sets the dragged
    // column's own width, floored at its own minimum — it has no notion of the table's outer
    // bound or of the other columns, so dragging one column wider just pushes the whole table
    // past the page edge.
    //
    // Catching this cheaply is trickier than it looks: setNodeMarkup (which the resize plugin
    // uses to write the new colwidth) produces a ReplaceAroundStep on non-leaf nodes like table
    // cells, never an AttrStep — AttrStep only comes from the separate setNodeAttribute API,
    // which nothing here calls. So rather than inspect step types (tying this to prosemirror-
    // tables' internal implementation choice), detect a resize by comparing each table's
    // resolved column widths before/after, gated by a cheap "did the document's total size stay
    // the same" pre-check — typing/row/column edits change size, a pure attribute change like a
    // resize doesn't, so that pre-check skips the table scan on every keystroke elsewhere in doc.
    let viewRef: EditorView | null = null;

    // Hard backstop: if something ever corrects far more than a real user gesture could cause in
    // one burst, stop and report it rather than let the tab hang. Shouldn't fire in practice —
    // see writeColumnWidths's comment for the bug (fixTables fighting a row-0-only write) that
    // used to make this trip routinely.
    let correctionsThisBurst = 0;
    let burstResetScheduled = false;
    const kMaxCorrectionsPerBurst = 5;

    return [
      ...parentPlugins,
      new Plugin({
        key: columnWidthGuardKey,
        view(editorView) {
          viewRef = editorView;
          syncTableAlign(editorView);
          return {
            update(updatedView, prevState) {
              if (prevState.doc !== updatedView.state.doc) {
                syncTableAlign(updatedView);

                // Runs here (the view's post-update hook), not in appendTransaction above:
                // appendTransaction fires before the view's DOM has been synced to newState, so
                // for structural changes that just introduced a table the DOM has no node yet at
                // its new position — view.nodeDOM(pos) then resolves to some unrelated, wrongly-
                // sized element (observed: a pasted table's containerWidth landing on its own
                // column-width sum instead of the actual editing canvas width). By the time this
                // update() hook runs, the DOM sync is complete, so the same measurement is
                // reliable here.
                const refresh = refreshContainerWidths(updatedView, updatedView.state);
                if (refresh) updatedView.dispatch(refresh.tr);
              }
            },
            destroy() {
              viewRef = null;
            },
          };
        },
        state: {
          init(_config, state) {
            return buildTableWidthHandleDecorations(state.doc, DecorationSet.empty);
          },
          apply(tr, oldSet, _oldState, newState) {
            if (!tr.docChanged) return oldSet;
            return buildTableWidthHandleDecorations(newState.doc, oldSet.map(tr.mapping, tr.doc));
          },
        },
        props: {
          decorations(state) {
            return columnWidthGuardKey.getState(state);
          },
        },
        appendTransaction(transactions, oldState, newState) {
          if (!viewRef) return null;
          if (!transactions.some((tr) => tr.docChanged)) return null;
          // Our own correction (below) also touches every column's colwidth. If a plugin
          // returns a transaction, ProseMirror re-runs appendTransaction against that result —
          // without this guard, our own correction would look like another resize on that
          // retry and get redistributed again against the wrong baseline.
          if (transactions.some((tr) => tr.getMeta(columnWidthGuardKey))) return null;

          if (newState.doc.content.size !== oldState.doc.content.size) {
            // Not a pure resize (column/row add/delete, a fresh table insert, moveColumn,
            // toggling a header row, ...) — writeColumnWidths never runs for these, so
            // containerWidth would otherwise go stale. Nothing here needs the width-
            // redistribution logic below: none of these actions can push a table wider than its
            // container, only narrower, which is now an accepted state — see
            // clampOverflowToMaxWidth's doc comment. The actual refresh happens in this plugin's
            // view.update() hook instead of here — see its comment for why appendTransaction is
            // too early to measure DOM for structural changes like a fresh table paste.
            return null;
          }

          if (correctionsThisBurst >= kMaxCorrectionsPerBurst) {
            console.error(
              "[table-resize] bailing after",
              kMaxCorrectionsPerBurst,
              "corrections in one burst — something isn't converging; not applying further corrections."
            );
            return null;
          }

          const result = findAndRedistributeResizedTable(viewRef, oldState, newState);
          if (result) {
            correctionsThisBurst += 1;
            if (!burstResetScheduled) {
              burstResetScheduled = true;
              setTimeout(() => {
                correctionsThisBurst = 0;
                burstResetScheduled = false;
              }, 500);
            }
          }
          return result?.tr ?? null;
        },
      }),
    ];
  },
}).configure({ resizable: true, lastColumnResizable: false, cellMinWidth: kMinColumnWidth });

/**
 * Wraps prosemirror-tables' addRowBefore/addRowAfter so the newly-inserted row's cells inherit
 * `backgroundColor` from the adjacent "anchor" row (the row the command was triggered from),
 * instead of prosemirror-tables' own default: `addRow` only copies the reference row's cell
 * *type* (cell vs header) via `type.createAndFill()`, never its attrs, so every new row always
 * came out with default (unstyled) cells regardless of the table's own shading.
 *
 * Runs the stock command with a captured dispatch (rather than reimplementing row insertion) so
 * behaviour otherwise stays identical — this only patches the resulting transaction before it's
 * actually dispatched.
 */
function withRowStyleCopy(
  command: (...args: any[]) => any,
  computeIndices: (rect: ReturnType<typeof selectedRect>) => { newRow: number; refRow: number }
) {
  return () => (props: any) => {
    const { state, dispatch } = props;
    if (!dispatch) return command()(props);

    const table = findTable(state.selection);
    if (!table) return command()(props);

    const { newRow, refRow } = computeIndices(selectedRect(state));

    let committedTr: Transaction | null = null;
    const ok = command()({
      ...props,
      dispatch: (tr: Transaction) => {
        committedTr = tr;
      },
    });
    if (!ok || !committedTr) return ok;

    let tr: Transaction = committedTr;
    const newTableNode = tr.doc.nodeAt(table.pos);
    if (newTableNode) {
      const map = TableMap.get(newTableNode);
      const written = new Set<number>();

      for (let col = 0; col < map.width; col += 1) {
        const newIndex = newRow * map.width + col;
        // A column covered by a rowspan reaching down from the row above isn't a genuinely new
        // cell — addRow grows that cell's rowspan instead of creating one here — so its existing
        // colour must be left alone, not overwritten from refRow.
        const coveredFromAbove = newRow > 0 && map.map[newIndex] === map.map[newIndex - map.width];
        if (coveredFromAbove) continue;

        const newPos = map.map[newIndex];
        if (written.has(newPos)) continue;
        written.add(newPos);

        const newCell = newTableNode.nodeAt(newPos);
        const refCell = newTableNode.nodeAt(map.map[refRow * map.width + col]);
        if (!newCell || !refCell) continue;
        if (newCell.attrs.backgroundColor === refCell.attrs.backgroundColor) continue;

        tr = tr.setNodeMarkup(table.start + newPos, undefined, {
          ...newCell.attrs,
          backgroundColor: refCell.attrs.backgroundColor,
        });
      }
    }

    dispatch(tr);
    return true;
  };
}

/**
 * Available content width for a table's own container (its DOM parent, minus that parent's own
 * padding) — the "100%" a full-width table is actually allowed to fill. Measured live via the
 * DOM rather than a fixed page-geometry constant so it stays correct at any --editor-scale zoom
 * level and for tables nested inside a narrower Columns block. Null if not mounted/measurable.
 */
function getTableContainerWidth(view: EditorView, tablePos: number): number | null {
  const wrapperDom = view.nodeDOM(tablePos) as HTMLElement | null;
  const parent = wrapperDom?.parentElement;
  if (!parent) return null;

  const parentStyle = window.getComputedStyle(parent);
  const width =
    parent.clientWidth - (parseFloat(parentStyle.paddingLeft) || 0) - (parseFloat(parentStyle.paddingRight) || 0);

  return width > 0 ? width : null;
}

/**
 * Keeps every table's `containerWidth` attr in sync with its live measured container width.
 * `writeColumnWidths` already does this for resize-shaped transactions (same doc size); this
 * covers everything else that changes a table's structure without going through it — column/row
 * add/delete, a fresh table insert, moveColumn, toggling a header row. Without it, containerWidth
 * silently goes stale (stuck at null, or at whatever it was before) and pdf-report-core falls
 * back to stretching the table to fill the page instead of rendering it at its true width — see
 * pdf-report-core's `_getTableColumnWidths`.
 */
function refreshContainerWidths(view: EditorView, state: EditorState): { tr: Transaction } | null {
  let tr: Transaction | null = null;

  state.doc.descendants((node, pos) => {
    if (node.type.spec.tableRole !== "table") return true;

    const maxWidth = getTableContainerWidth(view, pos);
    if (maxWidth !== null && Math.round(maxWidth) !== node.attrs.containerWidth) {
      tr = (tr ?? state.tr.setMeta(columnWidthGuardKey, true)).setNodeMarkup(pos, undefined, {
        ...node.attrs,
        containerWidth: Math.round(maxWidth),
      });
    }

    return false;
  });

  return tr ? { tr } : null;
}

/** Every column's width: its explicit `colwidth` if set, else its current fair share of whatever's left once the explicit columns are accounted for (mirrors what table-layout:fixed is actually rendering for an unset column). Always sums to ~maxWidth. */
function resolveColumnWidths(map: TableMap, tableNode: PMNode, maxWidth: number): number[] {
  const explicit: (number | null)[] = new Array(map.width).fill(null);

  for (let col = 0; col < map.width; col += 1) {
    const cellPos = map.positionAt(0, col, tableNode);
    const cellNode = tableNode.nodeAt(cellPos);
    if (!cellNode) continue;

    const cellStartCol = map.colCount(cellPos);
    const widthIndex = col - cellStartCol;
    const width = cellNode.attrs.colwidth?.[widthIndex];
    if (width) explicit[col] = width;
  }

  const explicitSum = explicit.reduce((sum: number, w) => sum + (w ?? 0), 0);
  const unsetCount = explicit.filter((w) => w === null).length;
  const fairShare = unsetCount > 0 ? Math.max(kMinColumnWidth, (maxWidth - explicitSum) / unsetCount) : 0;

  return explicit.map((w) => w ?? fairShare);
}

/**
 * Proportionally shrinks `widths` (respecting the `kMinColumnWidth` floor) until they sum to at
 * most `maxWidth`. Only ever corrects overflow — a total under `maxWidth` is left untouched, since
 * a table narrower than its container is now an accepted, persistent state (e.g. after deleting a
 * column) rather than something every correction pass should silently force back up to 100%.
 * Restoring full width is the table-width handle's job (see createTableWidthHandle), not an
 * automatic side effect of an unrelated column resize.
 */
function clampOverflowToMaxWidth(widths: number[], maxWidth: number): number[] {
  let result = widths.slice();

  // A handful of passes is enough to converge for our small (<=8) column counts: each pass pins
  // any column that shrinking would push under the floor, then re-spreads the remaining overage
  // across whatever's left.
  for (let pass = 0; pass < result.length + 1; pass += 1) {
    const total = result.reduce((sum, w) => sum + w, 0);
    const overBy = total - maxWidth;
    if (overBy < 0.5) break;

    const flexible = result.map((_, i) => i).filter((i) => result[i] > kMinColumnWidth);
    if (flexible.length === 0) break; // everyone's already at the floor.

    const flexibleTotal = flexible.reduce((sum, i) => sum + result[i], 0);
    flexible.forEach((i) => {
      const share = (result[i] / flexibleTotal) * overBy;
      result[i] = Math.max(kMinColumnWidth, result[i] - share);
    });
  }

  return result;
}

/**
 * Applies a resize of `resizedCol` to `targetWidth` in place on `widths`.
 *
 * Growing: first eats into any spare room below `maxWidth` (the gap between the table's current
 * total and its container) for free — a table currently narrower than 100% has room to grow
 * without touching its neighbours. Once that spare room is exhausted, further growth pulls from
 * the other columns' slack above `kMinColumnWidth` (proportionally; capped at however much slack
 * actually exists), so the total can never exceed `maxWidth` regardless of how far past it the
 * user drags.
 *
 * Shrinking: freed width is NOT handed back to the other columns — the total simply drops, since a
 * table narrower than its container is an accepted end state (see clampOverflowToMaxWidth).
 * Restoring full width is the table-width handle's job, not an automatic side effect of shrinking
 * one column.
 */
function applyColumnResize(widths: number[], resizedCol: number, targetWidth: number, maxWidth: number): number[] {
  const oldWidth = widths[resizedCol];
  const clampedTarget = Math.max(kMinColumnWidth, targetWidth);
  const delta = clampedTarget - oldWidth;
  const others = widths.map((_, col) => col).filter((col) => col !== resizedCol);

  if (delta > 0) {
    const currentTotal = widths.reduce((sum, w) => sum + w, 0);
    const spareRoom = Math.max(0, maxWidth - currentTotal);
    const fromSpare = Math.min(delta, spareRoom);
    const remaining = delta - fromSpare;

    let takenFromSiblings = 0;
    if (remaining > 0) {
      const totalSlack = others.reduce((sum, col) => sum + Math.max(0, widths[col] - kMinColumnWidth), 0);
      takenFromSiblings = Math.min(remaining, totalSlack);

      if (takenFromSiblings > 0) {
        others.forEach((col) => {
          const slack = Math.max(0, widths[col] - kMinColumnWidth);
          if (slack <= 0) return;
          widths[col] -= (slack / totalSlack) * takenFromSiblings;
        });
      }
    }
    widths[resizedCol] = oldWidth + fromSpare + takenFromSiblings;
  } else if (delta < 0) {
    widths[resizedCol] = clampedTarget;
  }

  return widths;
}

/**
 * Writes explicit per-column widths onto every row's cell for each column (handling colspan's
 * multi-entry colwidth array), rounding to whole pixels. Also stamps the table's own
 * `containerWidth` attr with `maxWidth` — the reference these pixel values are relative to — so
 * pdf-report-core can later scale them correctly instead of assuming the table always fills its
 * container (see containerWidth's addAttributes comment).
 *
 * colwidth is stored per-cell, not centrally on the column — prosemirror-tables' own
 * updateColumnWidth writes it to every row for exactly this reason. Writing only row 0 leaves
 * rows 1+ with stale colwidth for the same column; fixTables (part of prosemirror-tables'
 * tableEditing plugin, already active via appendTransaction) then detects that mismatch and
 * "fixes" it by overwriting row 0 back to match the stale rows — silently undoing this
 * correction on the very next transaction. (This was the actual cause of a very confusing
 * non-convergence bug — worth keeping this comment as a warning not to "simplify" this back to
 * row-0-only.)
 */
function writeColumnWidths(
  tr: Transaction,
  map: TableMap,
  tableNode: PMNode,
  tableStart: number,
  widths: number[],
  maxWidth: number
) {
  const written = new Set<number>();

  for (let row = 0; row < map.height; row += 1) {
    for (let col = 0; col < map.width; col += 1) {
      const mapIndex = row * map.width + col;
      // Skip cells covered by a rowspan from the row above — only write each origin cell once.
      if (row && map.map[mapIndex] === map.map[mapIndex - map.width]) continue;

      const cellPos = map.map[mapIndex];
      if (written.has(cellPos)) continue;
      written.add(cellPos);

      const cellNode = tableNode.nodeAt(cellPos);
      if (!cellNode) continue;

      const cellStartCol = map.colCount(cellPos);
      const colspan = cellNode.attrs.colspan ?? 1;
      const colwidth = new Array(colspan)
        .fill(0)
        .map((_, k) => Math.round(widths[cellStartCol + k] ?? kMinColumnWidth));

      tr.setNodeMarkup(tableStart + cellPos, undefined, { ...cellNode.attrs, colwidth });
    }
  }

  const tablePos = tableStart - 1;
  tr.setNodeMarkup(tablePos, undefined, { ...tableNode.attrs, containerWidth: Math.round(maxWidth) });
}

/**
 * Scans every table in `newState.doc` for one whose column widths differ from `oldState.doc`
 * (same absolute positions are valid in both, since the caller already checked total doc size
 * is unchanged) and, if found, redistributes that resize so the table can't exceed its
 * container — see applyColumnResize. Only ever corrects the first such table it finds; a single
 * user gesture only ever resizes one column in one table at a time.
 */
function findAndRedistributeResizedTable(
  view: EditorView,
  oldState: EditorState,
  newState: EditorState
): { tr: Transaction } | null {
  let result: { tr: Transaction } | null = null;

  newState.doc.descendants((node, pos) => {
    if (result) return false;
    if (node.type.spec.tableRole !== "table") return true;

    try {
      const oldNode = oldState.doc.nodeAt(pos);
      if (!oldNode || oldNode.type !== node.type) return true;

      const maxWidth = getTableContainerWidth(view, pos);
      if (maxWidth === null) return true;

      const map = TableMap.get(node);
      const oldMap = TableMap.get(oldNode);
      if (map.width !== oldMap.width || map.height !== oldMap.height) return true;

      const newWidths = resolveColumnWidths(map, node, maxWidth);
      const oldWidths = resolveColumnWidths(oldMap, oldNode, maxWidth);

      // A single user drag should only ever touch one column, but multiple can differ in the
      // same before/after comparison (e.g. two overlapping resize handlers firing off one
      // mouseup) — collect every column that actually changed and apply each in turn against
      // the same evolving widths array, rather than assume there's exactly one.
      const changedCols: number[] = [];
      for (let col = 0; col < map.width; col += 1) {
        if (Math.abs(newWidths[col] - oldWidths[col]) > 0.5) changedCols.push(col);
      }
      if (changedCols.length === 0) return true;
      if (changedCols.length > 1) {
        console.warn(
          `[table-resize] table@${pos}: ${changedCols.length} columns changed in one pass (expected 1 from a single drag) — applying each in turn`
        );
      }

      const baseline = clampOverflowToMaxWidth(oldWidths, maxWidth);
      let finalWidths = baseline.slice();
      for (const col of changedCols) {
        finalWidths = applyColumnResize(finalWidths, col, newWidths[col], maxWidth);
      }

      // If the redistributed result is already what's on screen (within rounding), writing it
      // back would be a pointless transaction that could itself look like "another resize" on
      // a later pass — skip it.
      const alreadyCorrect = finalWidths.every((w, i) => Math.abs(w - newWidths[i]) < 0.5);
      if (alreadyCorrect) return true;

      const tr = newState.tr.setMeta(columnWidthGuardKey, true);
      writeColumnWidths(tr, map, node, pos + 1, finalWidths, maxWidth);

      if (tr.docChanged) result = { tr };
    } catch (error) {
      console.error(`[table-resize] table@${pos}: exception while redistributing column widths`, error);
    }

    return false;
  });

  return result;
}

/**
 * Applies each table's `align` attr to its live `<table>` DOM element via `data-align` (read by
 * table.css). Needed because @tiptap/extension-table's built-in table nodeview builds `<table>`
 * with a plain `document.createElement`, bypassing the schema's renderHTML entirely — so setting
 * `align` in addAttributes alone has no visible effect. Safe to mutate directly (unlike the
 * table-width handle, which needed a decoration instead): that nodeview's own ignoreMutation
 * already whitelists *attribute* mutations on `this.table`, only childList mutations (e.g.
 * appending a DOM node) get reverted.
 */
function syncTableAlign(view: EditorView) {
  view.state.doc.descendants((node, pos) => {
    if (node.type.spec.tableRole !== "table") return true;

    const wrapperDom = view.nodeDOM(pos) as HTMLElement | null;
    const tableDom = wrapperDom?.querySelector(":scope > table") as HTMLElement | null;
    if (tableDom) {
      const align: TableAlign =
        node.attrs.align === "center" || node.attrs.align === "right" ? node.attrs.align : "left";
      if (align === kDefaultTableAlign) delete tableDom.dataset.align;
      else if (tableDom.dataset.align !== align) tableDom.dataset.align = align;
    }

    return false;
  });
}

const kTableWidthHandleClass = "table-width-handle";

/**
 * Incrementally adds a table-width-handle widget decoration for any table in `doc` that doesn't
 * already have one in `existingSet` — existing widgets (and their live DOM/listeners) are left
 * untouched, so a handle mid-drag never gets torn down by an unrelated edit elsewhere in the doc.
 */
function buildTableWidthHandleDecorations(doc: PMNode, existingSet: DecorationSet): DecorationSet {
  let set = existingSet;

  doc.descendants((node, pos) => {
    if (node.type.spec.tableRole !== "table") return true;

    // Last valid position inside the table (just after its last row) — rendered by ProseMirror
    // as an extra child of the table's contentDOM (its <tbody>).
    const handlePos = pos + node.nodeSize - 1;
    const alreadyPresent = set.find(handlePos, handlePos, (spec) => spec.isTableWidthHandle).length > 0;
    if (!alreadyPresent) {
      set = set.add(doc, [createTableWidthHandleWidget(handlePos)]);
    }

    return false;
  });

  return set;
}

/** Table (position + node) that a table-width-handle widget at `widgetPos` belongs to, or null if it's no longer in the doc. */
function findOwningTable(view: EditorView, widgetPos: number | undefined) {
  if (widgetPos === undefined) return null;
  const $pos = view.state.doc.resolve(widgetPos);
  return findParentNodeClosestToPos($pos, (node) => node.type.spec.tableRole === "table");
}

/**
 * Widget decoration for the table-width drag handle — the deliberate way to grow a table back
 * toward its container width once a column delete or shrink has left it under 100% (see
 * clampOverflowToMaxWidth/applyColumnResize's doc comments for why that's no longer done
 * automatically), or to shrink it further.
 *
 * Rendered as a decoration rather than a manually-appended DOM node: @tiptap/extension-table's
 * built-in table view only whitelists attribute mutations on <table>/<colgroup> in its own
 * ignoreMutation, so any other DOM child appended directly got silently stripped back out by
 * ProseMirror's DOM-sync on the very next update. Decorations are rendered by ProseMirror itself,
 * so they never hit that path. `getPos` (rather than a captured position) is used throughout so
 * the handle keeps working correctly as the table moves around the doc.
 *
 * Live-drags a plain DOM width for visual feedback only; the actual colwidth correction is
 * committed as a single transaction on mouseup, same timing as every other column-width
 * correction in this file.
 */
function createTableWidthHandleWidget(handlePos: number): Decoration {
  return Decoration.widget(
    handlePos,
    (view, getPos) => {
      const handle = document.createElement("div");
      handle.className = kTableWidthHandleClass;

      handle.addEventListener("mousedown", (event) => {
        event.preventDefault();
        event.stopPropagation();

        const table = findOwningTable(view, getPos());
        if (!table) return;

        const maxWidth = getTableContainerWidth(view, table.pos);
        if (maxWidth === null) return;

        const map = TableMap.get(table.node);
        const startWidths = resolveColumnWidths(map, table.node, maxWidth);
        const startTotal = startWidths.reduce((sum, w) => sum + w, 0);
        const minTotal = map.width * kMinColumnWidth;
        const startX = event.clientX;

        handle.classList.add("dragging");

        const requestedTotal = (clientX: number) => Math.max(minTotal, startTotal + (clientX - startX));

        // Same scaling used for both the live drag preview and the final committed write, so the
        // table never "jumps" at mouseup.
        const scaleWidthsTo = (total: number) => {
          const scale = startTotal > 0 ? total / startTotal : 1;
          return startWidths.map((w) => Math.max(kMinColumnWidth, w * scale));
        };

        const liveTableParts = () => {
          const wrapperDom = view.nodeDOM(table.pos) as HTMLElement | null;
          const tableDom = wrapperDom?.querySelector(":scope > table") as HTMLElement | null;
          const cols = tableDom
            ? (Array.from(tableDom.querySelectorAll(":scope > colgroup > col")) as HTMLElement[])
            : [];
          return { tableDom, cols };
        };

        // Visual-only preview — no transaction dispatched until mouseup. Setting the table's own
        // width alone isn't enough to preview shrinking: with table-layout:fixed, the rendered
        // width is the *greater* of the table's declared width and the sum of its (still
        // uncommitted) <col> widths, so a smaller requested width is silently ignored until the
        // columns themselves are previewed smaller too — hence updating both here.
        const previewWidth = (total: number) => {
          const { tableDom, cols } = liveTableParts();
          if (!tableDom || cols.length !== startWidths.length) return;

          const widths = scaleWidthsTo(total);
          cols.forEach((col, i) => {
            col.style.width = `${Math.round(widths[i])}px`;
          });
          tableDom.style.width = `${total}px`;
        };

        const onMouseMove = (moveEvent: MouseEvent) => {
          // The view can be destroyed mid-drag (e.g. navigating away); bail rather than
          // operate on/read from a torn-down EditorView.
          if (!view.dom.isConnected) return;
          previewWidth(Math.min(requestedTotal(moveEvent.clientX), maxWidth));
        };

        const onMouseUp = (upEvent: MouseEvent) => {
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);

          if (!view.dom.isConnected) return;

          handle.classList.remove("dragging");

          const { tableDom, cols } = liveTableParts();
          if (tableDom) tableDom.style.width = "";
          cols.forEach((col) => {
            col.style.width = "";
          });

          const currentTable = findOwningTable(view, getPos());
          if (!currentTable) return;

          const currentMap = TableMap.get(currentTable.node);
          if (currentMap.width !== map.width) return; // columns changed mid-drag — bail clean, don't guess.

          const finalWidths = clampOverflowToMaxWidth(scaleWidthsTo(requestedTotal(upEvent.clientX)), maxWidth);

          const tr = view.state.tr.setMeta(columnWidthGuardKey, true);
          writeColumnWidths(tr, currentMap, currentTable.node, currentTable.pos + 1, finalWidths, maxWidth);
          if (tr.docChanged) view.dispatch(tr);
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
      });

      return handle;
    },
    { side: 1, isTableWidthHandle: true, stopEvent: () => true }
  );
}

export default Table;
